import {
  type Cell,
  type ColumnDef,
  type ColumnSizingState,
  columnResizingFeature,
  columnSizingFeature,
  flexRender,
  type Header,
  type Row,
  type Table,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { useRef, useState } from 'react';
import {
  type GestureResponderEvent,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import HoverTooltip from '@/components/UI/HoverTooltip';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import type {
  TableNextColumn,
  TableNextProps,
  TableNextRowData,
} from './TableNext.types';

const tableNextFeatures = tableFeatures({
  columnResizingFeature,
  columnSizingFeature,
});
type TableNextFeatures = typeof tableNextFeatures;
type TableNextCell<T> = Cell<TableNextFeatures, TableNextRowData<T>, unknown>;
type TableNextHeader<T> = Header<
  TableNextFeatures,
  TableNextRowData<T>,
  unknown
>;
type TableNextRow<T> = Row<TableNextFeatures, TableNextRowData<T>>;
type TableNextTable<T> = Table<TableNextFeatures, TableNextRowData<T>>;

type SortedCellStyleOptions = {
  isSorted: boolean;
};
type RowStyleOptions = {
  interactive: boolean;
};
type DividerStyleOptions = {
  showDivider: boolean;
};
type HeaderCellLayerOptions = {
  layer: number;
};
type HeaderButtonStyleOptions = {
  interactive: boolean;
};
type ColumnSizeState = {
  maxWidth: number;
  minWidth: number;
  width: number;
};
type ActiveColumnResizeState = {
  columnId: string;
  startOffset: number;
  maxWidth: number;
  minWidth: number;
  startPageX: number;
  startSize: number;
};
type ColumnResizeTapState = {
  columnId: string;
  timestamp: number;
};
type ResizeHandleStyleOptions = {
  active: boolean;
};
type ResizeGuideState = {
  columnId: string;
  left: number;
};
type ResizeGuideStyleOptions = {
  left: number;
};
type RenderHeaderCellOptions<T> = {
  collapsed: boolean;
  header: TableNextHeader<T>;
  headers: TableNextHeader<T>[];
  index: number;
  sectionStartOffset: number;
};
type RenderRowOptions<T> = {
  cells: TableNextCell<T>[];
  collapsed: boolean;
  row: TableNextRow<T>;
};

const style = StyleSheet.create((theme) => ({
  table: {
    alignSelf: 'stretch',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  content: {
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: '100%',
    position: 'relative',
  },
  pinnedContent: {
    flexDirection: 'column',
  },
  tableBody: {
    flexDirection: 'row',
    alignSelf: 'stretch',
  },
  pinnedColumns: {
    backgroundColor: theme.colors.surface,
    flexShrink: 0,
    zIndex: 2,
  },
  pinnedColumnsDivider: {
    borderRightColor: theme.colors.borderMuted,
    borderRightWidth: StyleSheet.hairlineWidth * 2,
  },
  headerRow: {
    backgroundColor: theme.colors.surface,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
  emptyHeaderDivider: {
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerCell: {
    alignItems: 'stretch',
    borderRightColor: theme.colors.borderMuted,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    minHeight: theme.size(10),
    overflow: 'visible',
    paddingHorizontal: theme.space(3),
    paddingVertical: theme.space(2),
    position: 'relative',
  },
  headerCellSize: ({
    flexGrow,
    flexShrink,
    maxWidth,
    minWidth,
    width,
  }: ColumnSizeState & {
    flexGrow: number;
    flexShrink: number;
  }) => ({
    flexGrow,
    flexShrink,
    maxWidth,
    minWidth,
    width,
  }),
  headerCellLayer: ({ layer }: HeaderCellLayerOptions) => ({
    zIndex: layer,
  }),
  headerButton: ({ interactive }: HeaderButtonStyleOptions) => ({
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: theme.size(8),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
    _web: {
      cursor: interactive ? 'pointer' : 'default',
    },
  }),
  headerLabel: {
    flexShrink: 1,
    minWidth: 0,
  },
  resizeHandle: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    right: -theme.size(1),
    top: 0,
    width: theme.size(2),
    zIndex: 2,
    _web: {
      cursor: 'col-resize',
    },
  },
  resizeHandleLine: ({ active }: ResizeHandleStyleOptions) => ({
    backgroundColor: active ? theme.colors.primary : 'transparent',
    bottom: 0,
    left: '50%',
    marginLeft: -1,
    position: 'absolute',
    top: 0,
    width: 2,
  }),
  resizeGuide: ({ left }: ResizeGuideStyleOptions) => ({
    backgroundColor: theme.colors.primary,
    bottom: 0,
    left,
    marginLeft: -1,
    position: 'absolute',
    top: 0,
    width: 2,
    zIndex: 3,
  }),
  row: ({ interactive }: RowStyleOptions) => ({
    backgroundColor: theme.colors.surface,
    borderTopColor: theme.colors.borderMuted,
    borderTopWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    minHeight: theme.size(12),
    width: '100%',
    _web: {
      cursor: interactive ? 'pointer' : 'default',
      _hover: interactive
        ? {
            backgroundColor: theme.colors.surfaceSecondary,
          }
        : {},
    },
  }),
  rowActive: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
  cell: ({
    flexGrow,
    flexShrink,
    maxWidth,
    minWidth,
    width,
  }: ColumnSizeState & {
    flexGrow: number;
    flexShrink: number;
  }) => ({
    alignItems: 'flex-start',
    borderRightColor: theme.colors.borderMuted,
    flexGrow,
    flexShrink,
    justifyContent: 'center',
    maxWidth,
    minHeight: theme.size(12),
    minWidth,
    overflow: 'hidden',
    paddingHorizontal: theme.space(3),
    paddingVertical: theme.space(2),
    width,
  }),
  columnDivider: ({ showDivider }: DividerStyleOptions) =>
    showDivider
      ? {
          borderRightWidth: StyleSheet.hairlineWidth,
        }
      : {},
  sortedCell: ({ isSorted }: SortedCellStyleOptions) => {
    if (!isSorted) {
      return {};
    }
    return {
      backgroundColor: theme.colors.tableCellSorted,
    };
  },
  scrollView: {
    backgroundColor: theme.colors.surface,
  },
}));

const DEFAULT_COLUMN_MIN_WIDTH = 0;
const DEFAULT_COLUMN_MAX_WIDTH = 288;
const DEFAULT_COLUMN_WIDTH = 160;
const HEADER_AUTOSIZE_EXTRA_WIDTH = 40;
const CELL_AUTOSIZE_EXTRA_WIDTH = 32;
const APPROXIMATE_CHARACTER_WIDTH = 8;
const ARRAY_ITEM_PREVIEW_WIDTH = 32;
const COLUMN_AUTOSIZE_DOUBLE_TAP_DELAY_MS = 300;
const COLUMN_RESIZE_TAP_MOVEMENT_THRESHOLD = 4;

function clampColumnWidth({
  maxWidth,
  minWidth,
  width,
}: {
  maxWidth?: number;
  minWidth: number;
  width: number;
}) {
  return Math.max(minWidth, Math.min(width, maxWidth ?? width));
}

function getTextWidthEstimate(value: string, extraWidth: number) {
  return value.length * APPROXIMATE_CHARACTER_WIDTH + extraWidth;
}

function getHeaderWidthEstimate(title: string) {
  if (!title) {
    return DEFAULT_COLUMN_MIN_WIDTH;
  }

  return (
    title.length * APPROXIMATE_CHARACTER_WIDTH + HEADER_AUTOSIZE_EXTRA_WIDTH
  );
}

function getCellAutosizeExtraWidth<T>(column: TableNextColumn<T>) {
  return column.autosizeExtraWidth ?? CELL_AUTOSIZE_EXTRA_WIDTH;
}

function getColumnAutosizeCellValue<T>({
  column,
  rowData,
}: {
  column: TableNextColumn<T>;
  rowData: TableNextRowData<T>;
}) {
  if (column.getAutosizeValue) {
    return column.getAutosizeValue(rowData);
  }

  return rowData[column.id];
}

function getCellWidthEstimate<T>({
  cellValue,
  column,
}: {
  cellValue: unknown;
  column: TableNextColumn<T>;
}) {
  const extraWidth = getCellAutosizeExtraWidth(column);

  if (typeof cellValue === 'number') {
    return getTextWidthEstimate(String(cellValue), extraWidth);
  }

  if (typeof cellValue === 'string') {
    return getTextWidthEstimate(cellValue, extraWidth);
  }

  if (Array.isArray(cellValue)) {
    return cellValue.length * ARRAY_ITEM_PREVIEW_WIDTH + extraWidth;
  }

  return DEFAULT_COLUMN_WIDTH;
}

function getColumnContentWidth<T>({
  column,
  rows,
}: {
  column: TableNextColumn<T>;
  rows: TableNextRowData<T>[];
}) {
  return rows.reduce((widestCellWidth, rowData) => {
    const cellWidth = getCellWidthEstimate({
      cellValue: getColumnAutosizeCellValue({
        column,
        rowData,
      }),
      column,
    });

    return Math.max(widestCellWidth, cellWidth);
  }, DEFAULT_COLUMN_MIN_WIDTH);
}

function getColumnMinimumWidth<T>(column: TableNextColumn<T>) {
  return column.minWidth ?? getHeaderWidthEstimate(column.title);
}

function getColumnMaximumWidth<T>(column: TableNextColumn<T>) {
  const configuredMaximumWidth =
    column.maxWidth ?? Math.max(column.width ?? 0, DEFAULT_COLUMN_MAX_WIDTH);

  return Math.max(configuredMaximumWidth, getColumnMinimumWidth(column));
}

function getColumnCanResize<T>(column: TableNextColumn<T>) {
  if (column.pin === 'left') {
    return false;
  }

  return column.enableResizing ?? Boolean(column.title);
}

function getColumnSizeForRender<T>({
  collapsed,
  column,
  size,
}: {
  collapsed: boolean;
  column: TableNextColumn<T>;
  size: number;
}): ColumnSizeState {
  if (collapsed && column.pin === 'left' && column.collapsedWidth) {
    return {
      maxWidth: column.collapsedWidth,
      minWidth: column.collapsedWidth,
      width: column.collapsedWidth,
    };
  }

  return {
    maxWidth: getColumnMaximumWidth(column),
    minWidth: getColumnMinimumWidth(column),
    width: size,
  };
}

function getColumnIsPinnedLeft<T>(column: TableNextColumn<T>) {
  return column.pin === 'left';
}

function getPinnedColumnIds<T>(columns: TableNextColumn<T>[]) {
  return new Set(
    columns
      .filter((column) => getColumnIsPinnedLeft(column))
      .map((column) => column.id),
  );
}

function getPinnedHeaders<T>(
  headers: TableNextHeader<T>[],
  pinnedColumnIds: Set<string>,
) {
  return headers.filter((header) => pinnedColumnIds.has(header.column.id));
}

function getScrollableHeaders<T>(
  headers: TableNextHeader<T>[],
  pinnedColumnIds: Set<string>,
) {
  return headers.filter((header) => !pinnedColumnIds.has(header.column.id));
}

function getPinnedCells<T>(
  cells: TableNextCell<T>[],
  pinnedColumnIds: Set<string>,
) {
  return cells.filter((cell) => pinnedColumnIds.has(cell.column.id));
}

function getScrollableCells<T>(
  cells: TableNextCell<T>[],
  pinnedColumnIds: Set<string>,
) {
  return cells.filter((cell) => !pinnedColumnIds.has(cell.column.id));
}

function getHeaderWidths<T>(headers: TableNextHeader<T>[]) {
  return headers.reduce(
    (totalWidth, header) => totalWidth + header.getSize(),
    0,
  );
}

function getColumnPreferredWidth<T>({
  column,
  rows,
}: {
  column: TableNextColumn<T>;
  rows: TableNextRowData<T>[];
}) {
  if (column.width !== undefined) {
    return column.width;
  }

  const headerWidth = getHeaderWidthEstimate(column.title);
  const contentWidth = getColumnContentWidth({ column, rows });

  return Math.max(headerWidth, contentWidth);
}

function getColumnAutosizeWidth<T>({
  column,
  rows,
}: {
  column: TableNextColumn<T>;
  rows: TableNextRowData<T>[];
}) {
  const headerWidth = getHeaderWidthEstimate(column.title);
  const contentWidth = getColumnContentWidth({ column, rows });

  return Math.max(headerWidth, contentWidth);
}

function getColumnSizing<T>({
  column,
  rows,
}: {
  column: TableNextColumn<T>;
  rows: TableNextRowData<T>[];
}): ColumnSizeState {
  const minWidth = getColumnMinimumWidth(column);
  const maxWidth = getColumnMaximumWidth(column);
  const preferredWidth = getColumnPreferredWidth({ column, rows });
  const width = clampColumnWidth({
    maxWidth,
    minWidth,
    width: preferredWidth,
  });

  return {
    maxWidth,
    minWidth,
    width,
  };
}

function getColumnFlex<T>(column: TableNextColumn<T>) {
  return {
    flexGrow: column.flexGrow ?? column.flex ?? 0,
    flexShrink: column.flexShrink ?? 0,
  };
}

function getTableColumnById<T>({
  columns,
  id,
}: {
  columns: TableNextColumn<T>[];
  id: string;
}) {
  const tableColumn = columns.find((column) => column.id === id);

  if (!tableColumn) {
    throw new Error(`Missing TableNext column: ${id}`);
  }

  return tableColumn;
}

function getTanStackColumns<T>({
  columns,
  rows,
}: {
  columns: TableNextColumn<T>[];
  rows: TableNextRowData<T>[];
}): ColumnDef<TableNextFeatures, TableNextRowData<T>, unknown>[] {
  return columns.map((column) => {
    const sizing = getColumnSizing({ column, rows });

    return {
      id: column.id,
      accessorFn: (rowData) => rowData[column.id],
      cell: (cellContext) => cellContext.getValue(),
      enableResizing: getColumnCanResize(column),
      header: column.title,
      maxSize: sizing.maxWidth,
      minSize: sizing.minWidth,
      size: sizing.width,
    };
  });
}

function getResizePageX(event: GestureResponderEvent) {
  return event.nativeEvent.pageX;
}

function getResizedColumnWidth({
  activeResize,
  pageX,
}: {
  activeResize: ActiveColumnResizeState;
  pageX: number;
}) {
  return clampColumnWidth({
    maxWidth: activeResize.maxWidth,
    minWidth: activeResize.minWidth,
    width: activeResize.startSize + pageX - activeResize.startPageX,
  });
}

function getResizeGuideLeft(
  activeResize: ActiveColumnResizeState,
  width: number,
) {
  return activeResize.startOffset + width;
}

function setColumnWidth<T>({
  columnId,
  table,
  width,
}: {
  columnId: string;
  table: TableNextTable<T>;
  width: number;
}) {
  table.setColumnSizing((currentColumnSizing) => ({
    ...currentColumnSizing,
    [columnId]: width,
  }));
}

function setColumnAutosize<T>({
  column,
  rows,
  table,
}: {
  column: TableNextColumn<T>;
  rows: TableNextRowData<T>[];
  table: TableNextTable<T>;
}) {
  const nextWidth = clampColumnWidth({
    maxWidth: getColumnMaximumWidth(column),
    minWidth: getColumnMinimumWidth(column),
    width: getColumnAutosizeWidth({ column, rows }),
  });

  setColumnWidth({
    columnId: column.id,
    table,
    width: nextWidth,
  });
}

export default function TableNext<T>({
  columns,
  sortColumn = null,
  rows,
  onPressRow,
  header = null,
  emptyContent = null,
  highlight = false,
  getRowTestID,
}: TableNextProps<T>) {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [resizeGuide, setResizeGuide] = useState<ResizeGuideState | null>(null);
  const activeResizeRef = useRef<ActiveColumnResizeState | null>(null);
  const lastResizeTapRef = useRef<ColumnResizeTapState | null>(null);
  const table = useTable({
    columnResizeMode: 'onEnd',
    columns: getTanStackColumns({ columns, rows }),
    data: rows,
    enableColumnResizing: true,
    features: tableNextFeatures,
    getRowId: (rowData) => String(rowData.id),
    onColumnSizingChange: setColumnSizing,
    state: {
      columnSizing,
    },
  });

  const beginColumnResize = ({
    column,
    event,
    header,
    sectionStartOffset,
  }: {
    column: TableNextColumn<T>;
    event: GestureResponderEvent;
    header: TableNextHeader<T>;
    sectionStartOffset: number;
  }) => {
    if (!header.column.getCanResize()) {
      return;
    }

    event.stopPropagation();

    const activeResize = {
      columnId: header.column.id,
      startOffset: header.getStart() - sectionStartOffset,
      maxWidth: getColumnMaximumWidth(column),
      minWidth: getColumnMinimumWidth(column),
      startPageX: getResizePageX(event),
      startSize: header.getSize(),
    };

    activeResizeRef.current = activeResize;
    setResizeGuide({
      columnId: activeResize.columnId,
      left: getResizeGuideLeft(activeResize, activeResize.startSize),
    });
  };
  const updateColumnResize = (event: GestureResponderEvent) => {
    const activeResize = activeResizeRef.current;

    if (!activeResize) {
      return;
    }

    event.stopPropagation();

    const pageX = getResizePageX(event);
    const nextWidth = getResizedColumnWidth({
      activeResize,
      pageX,
    });

    setResizeGuide({
      columnId: activeResize.columnId,
      left: getResizeGuideLeft(activeResize, nextWidth),
    });
  };
  const endColumnResize = (event: GestureResponderEvent) => {
    const activeResize = activeResizeRef.current;

    updateColumnResize(event);
    activeResizeRef.current = null;
    setResizeGuide(null);

    if (!activeResize) {
      return;
    }

    const pageX = getResizePageX(event);
    const draggedDistance = Math.abs(pageX - activeResize.startPageX);

    if (draggedDistance > COLUMN_RESIZE_TAP_MOVEMENT_THRESHOLD) {
      setColumnWidth({
        columnId: activeResize.columnId,
        table,
        width: getResizedColumnWidth({
          activeResize,
          pageX,
        }),
      });
      lastResizeTapRef.current = null;
      return;
    }

    const timestamp = event.timeStamp;
    const lastResizeTap = lastResizeTapRef.current;
    const doubleTapped =
      lastResizeTap?.columnId === activeResize.columnId &&
      timestamp - lastResizeTap.timestamp <=
        COLUMN_AUTOSIZE_DOUBLE_TAP_DELAY_MS;

    if (!doubleTapped) {
      lastResizeTapRef.current = {
        columnId: activeResize.columnId,
        timestamp,
      };
      return;
    }

    const column = getTableColumnById({
      columns,
      id: activeResize.columnId,
    });

    setColumnAutosize({
      column,
      rows,
      table,
    });
    lastResizeTapRef.current = null;
  };

  const renderCell = (
    rowData: TableNextRowData<T>,
    cell: TableNextCell<T>,
    collapsed: boolean,
  ) => {
    const column = getTableColumnById({
      columns,
      id: cell.column.id,
    });

    if (collapsed && column.renderCollapsedCell) {
      return column.renderCollapsedCell(rowData);
    }

    if (column.renderCell) {
      return column.renderCell(rowData);
    }
    const cellValue = flexRender(cell.column.columnDef.cell, cell.getContext());
    const displayValue =
      typeof cellValue === 'number' || typeof cellValue === 'string'
        ? cellValue
        : 0;

    return (
      <Text
        numberOfLines={1}
        fontWeight={column.id === sortColumn?.id ? 'bold' : 'normal'}
        flexShrink={1}
        selectable
        size="md"
        testID="TableCell"
      >
        {displayValue || 0}
      </Text>
    );
  };
  const renderHeaderCell = ({
    collapsed,
    header,
    headers,
    index,
    sectionStartOffset,
  }: RenderHeaderCellOptions<T>) => {
    const column = getTableColumnById({
      columns,
      id: header.column.id,
    });
    const headerTitle = flexRender(
      header.column.columnDef.header,
      header.getContext(),
    );
    const columnSize = {
      ...getColumnFlex(column),
      ...getColumnSizeForRender({
        collapsed,
        column,
        size: header.getSize(),
      }),
    };
    const headerCellStyle = [
      style.headerCell,
      style.columnDivider({
        showDivider: index < headers.length - 1,
      }),
      column.headerCellStyle,
      style.headerCellSize(columnSize),
      style.headerCellLayer({
        layer: headers.length - index,
      }),
    ];
    const headerInteractive = Boolean(column.onPressHeader);
    const headerCellContent =
      collapsed && column.renderCollapsedHeaderCell ? (
        column.renderCollapsedHeaderCell(column)
      ) : column.renderHeaderCell ? (
        column.renderHeaderCell(column)
      ) : (
        <Pressable
          testID="TableHeaderCell"
          disabled={!headerInteractive}
          style={style.headerButton({ interactive: headerInteractive })}
          onPress={() => {
            if (column.onPressHeader) {
              column.onPressHeader(column);
            }
          }}
        >
          <Box
            direction="horizontal"
            align="center"
            justify="space-between"
            flex
            flexShrink={1}
            minWidth={0}
          >
            <Text
              numberOfLines={1}
              fontWeight={column.id === sortColumn?.id ? 'bold' : 'normal'}
              flexShrink={1}
              selectable
              size="sm"
              style={style.headerLabel}
              testID={`TableHeaderLabel-${column.id}`}
              variant="typographySecondary"
            >
              {headerTitle}
            </Text>
            {sortColumn?.id === column.id && (
              <Icon
                name={
                  sortColumn?.direction === 'asc'
                    ? 'arrow-up-outline'
                    : 'arrow-down-outline'
                }
                size="xs"
              />
            )}
          </Box>
        </Pressable>
      );
    const renderResizeHandle = () => {
      if (collapsed || !header.column.getCanResize()) {
        return null;
      }

      const active = resizeGuide?.columnId === header.column.id;

      return (
        <View
          onMoveShouldSetResponder={() => true}
          onResponderGrant={(event) => {
            beginColumnResize({ column, event, header, sectionStartOffset });
          }}
          onResponderMove={updateColumnResize}
          onResponderRelease={endColumnResize}
          onResponderTerminate={endColumnResize}
          onStartShouldSetResponder={() => true}
          style={style.resizeHandle}
          testID={`TableColumnResizeHandle-${column.id}`}
        >
          <View
            pointerEvents="none"
            style={style.resizeHandleLine({ active })}
            testID={`TableColumnResizeHandleLine-${column.id}`}
          />
        </View>
      );
    };

    if (!column.tooltip) {
      return (
        <View
          key={column.id}
          testID="TableHeaderCellHost"
          style={headerCellStyle}
        >
          {headerCellContent}
          {renderResizeHandle()}
        </View>
      );
    }

    return (
      <View
        key={column.id}
        testID="TableHeaderCellHost"
        style={headerCellStyle}
      >
        <HoverTooltip label={column.tooltip}>{headerCellContent}</HoverTooltip>
        {renderResizeHandle()}
      </View>
    );
  };
  const renderRow = ({ cells, collapsed, row }: RenderRowOptions<T>) => {
    const rowData = row.original;
    const interactive = Boolean(onPressRow);

    return (
      <Pressable
        onPress={() => onPressRow?.(rowData)}
        key={rowData.id}
        style={({ pressed }) => [
          style.row({
            interactive,
          }),
          interactive && pressed ? style.rowActive : null,
        ]}
        testID={getRowTestID?.(rowData) ?? 'TableRow'}
      >
        {cells.map((cell, columnIndex) => {
          const column = getTableColumnById({
            columns,
            id: cell.column.id,
          });
          const columnSize = {
            ...getColumnFlex(column),
            ...getColumnSizeForRender({
              collapsed,
              column,
              size: cell.column.getSize(),
            }),
          };

          return (
            <View
              style={[
                style.cell(columnSize),
                style.columnDivider({
                  showDivider: columnIndex < cells.length - 1,
                }),
                column.cellStyle,
                style.sortedCell({
                  isSorted: highlight && column.id === sortColumn?.id,
                }),
              ]}
              key={cell.id}
              testID="TableCellHost"
            >
              {renderCell(rowData, cell, collapsed)}
            </View>
          );
        })}
      </Pressable>
    );
  };

  const pinnedColumnIds = getPinnedColumnIds(columns);
  const hasPinnedColumns = pinnedColumnIds.size > 0;

  return (
    <>
      {header}
      <View style={style.table} testID="TableNext">
        <View style={style.tableBody}>
          {hasPinnedColumns ? (
            <View
              style={[style.pinnedColumns, style.pinnedColumnsDivider]}
              testID="TableNextPinnedColumns"
            >
              <View style={style.pinnedContent}>
                {table.getHeaderGroups().map((headerGroup) => {
                  const pinnedHeaders = getPinnedHeaders(
                    headerGroup.headers,
                    pinnedColumnIds,
                  );

                  return (
                    <View
                      key={headerGroup.id}
                      style={[
                        style.headerRow,
                        rows.length === 0 && style.emptyHeaderDivider,
                      ]}
                      testID="TablePinnedHeaderRow"
                    >
                      {pinnedHeaders.map((pinnedHeader, index) =>
                        renderHeaderCell({
                          collapsed: false,
                          header: pinnedHeader,
                          headers: pinnedHeaders,
                          index,
                          sectionStartOffset: 0,
                        }),
                      )}
                    </View>
                  );
                })}
                {table.getRowModel().rows.map((row) =>
                  renderRow({
                    cells: getPinnedCells(row.getAllCells(), pinnedColumnIds),
                    collapsed: false,
                    row,
                  }),
                )}
              </View>
            </View>
          ) : null}

          <ScrollView
            nestedScrollEnabled
            horizontal
            style={style.scrollView}
            contentContainerStyle={style.content}
            bounces={false}
            testID="TableNextScrollableColumns"
          >
            {table.getHeaderGroups().map((headerGroup) => {
              const pinnedHeaders = getPinnedHeaders(
                headerGroup.headers,
                pinnedColumnIds,
              );
              const scrollableHeaders = getScrollableHeaders(
                headerGroup.headers,
                pinnedColumnIds,
              );
              const pinnedHeaderWidth = getHeaderWidths(pinnedHeaders);

              return (
                <View
                  key={headerGroup.id}
                  style={[
                    style.headerRow,
                    rows.length === 0 && style.emptyHeaderDivider,
                  ]}
                  testID="TableHeaderRow"
                >
                  {scrollableHeaders.map((scrollableHeader, index) =>
                    renderHeaderCell({
                      collapsed: false,
                      header: scrollableHeader,
                      headers: scrollableHeaders,
                      index,
                      sectionStartOffset: pinnedHeaderWidth,
                    }),
                  )}
                </View>
              );
            })}
            {table.getRowModel().rows.map((row) =>
              renderRow({
                cells: getScrollableCells(row.getAllCells(), pinnedColumnIds),
                collapsed: false,
                row,
              }),
            )}
            {resizeGuide ? (
              <View
                pointerEvents="none"
                style={style.resizeGuide({ left: resizeGuide.left })}
                testID="TableColumnResizeGuide"
              />
            ) : null}
          </ScrollView>
        </View>
        {emptyContent}
      </View>
    </>
  );
}
